const gcd = (a, b) => b ? gcd(b, a % b) : Math.abs(a);

const MAX_WIDTH = 640;
const MAX_HEIGHT = 360;

function estimateAspectRatio(width, height) {
    const ratio = gcd(width, height);
    width /= ratio;
    height /= ratio;
    return [width, height];
}

function constrainSize(w, h, mw, mh) {
    mw ??= 0; mh ??= 0;
    w ??= 0; h ??= 0;

    const ar = estimateAspectRatio(w, h);

    if(w > h) {
        if(mw > 0) {
            w = Math.min(mw, w);
            h = Math.ceil((w / ar[0]) * ar[1]);
            if(mh > 0) h = Math.min(mh, h);
        }
    } else {
        if(mh > 0) {
            h = Math.min(mh, h);
            w = Math.ceil((h / ar[1]) * ar[0]);
            if(mw > 0) w = Math.min(mw, w);
        }
    }

    return [w, h];
}

export class VideoEmbed {
    #player;
    #element;

    get element() { return this.#element; }
    get player() { return this.#player; }

    constructor(player) {
        this.#player = player;
        this.#element = <div class={`embed embed-${this.#player.type}`}>{this.#player}</div>;
    }
}

export class VideoEmbedPlayer {
    #element;
    #initialSize;

    get element() { return this.#element; }
    get type() { return 'external'; }

    constructor(metadata) {
        this.#initialSize = this.#constrainSize(metadata.width ?? 200, metadata.height ?? 200);
        this.#element = <video src={metadata.url} autoplay={true} controls={true} style={{ width: `${this.#initialSize[0]}px`, height: `${this.#initialSize[1]}px` }}
            onresize={() => { this.#setSize(this.#element.videoWidth, this.#element.videoHeight) }} />;
    }

    #constrainSize(w, h, mw, mh) {
        return constrainSize(w, h, mw ?? MAX_WIDTH, mh ?? MAX_HEIGHT);
    }

    #setSize(w, h) {
        const size = this.#constrainSize(w, h, this.#initialSize[0], this.#initialSize[1]);
        this.#element.style.width = `${size[0]}px`;
        this.#element.style.height = `${size[1]}px`;
    };
}

export class VideoEmbedYouTube {
    #element;

    get element() { return this.#element; }
    get type() { return 'youtube'; }

    constructor(metadata) {
        let embedUrl = `https://www.youtube.com/embed/${metadata.youtube_video_id}?autoplay=1`;
        if(metadata.youtube_start_time)
            embedUrl += `&t=${encodeURIComponent(metadata.youtube_start_time)}`;
        if(metadata.youtube_playlist) {
            embedUrl += `&list=${encodeURIComponent(metadata.youtube_playlist)}`;
            if(metadata.youtube_playlist_index)
                embedUrl += `&index=${encodeURIComponent(metadata.youtube_playlist_index)}`;
        }

        this.#element = <iframe frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen={true} src={embedUrl} />;
    }
}

export class VideoEmbedNicoNico {
    #element;

    get element() { return this.#element; }
    get type() { return 'nicovideo'; }

    constructor(metadata) {
        let embedUrl = `https://embed.nicovideo.jp/watch/${metadata.nicovideo_video_id}?autoplay=1`;
        if(metadata.nicovideo_start_time)
            embedUrl += `&from=${encodeURIComponent(metadata.nicovideo_start_time)}`;

        this.#element = <iframe frameborder="0" allow="autoplay" allowfullscreen={true} src={embedUrl} />;
    }
}

export class VideoEmbedPlaceholder {
    #element;

    get element() { return this.#element; }

    constructor(metadata, playerType, { type='external', autosize=false }) {
        if(typeof playerType !== 'function')
            throw new Error('playerType must be a function');

        const infoChildren = [];
        infoChildren.push(<h1 class="embedph-info-title">{metadata.title}</h1>);

        if(metadata.description) {
            let firstLine = metadata.description.split("\n")[0].trim();
            if(firstLine.length > 300)
                firstLine = firstLine.substring(0, 300).trim() + '...';

            infoChildren.push(<div class="embedph-info-desc">{firstLine}</div>);
        }

        infoChildren.push(<div class="embedph-info-site">{metadata.site_name}</div>);

        const style = {};
        if(typeof metadata.color !== 'undefined')
            style['--embedph-colour'] = metadata.color;

        if(autosize) {
            const size = constrainSize(
                metadata.width ?? 200,
                metadata.height ?? 200,
                MAX_WIDTH,
                MAX_HEIGHT
            );

            style['width'] = `${size[0]}px`;
            style['height'] = `${size[1]}px`;
        }

        this.#element = <div class={`embedph embedph-${type}`} style={style}>
            <div class="embedph-bg">
                <img src={metadata.image} alt="" />
            </div>
            <div class="embedph-fg">
                <div class="embedph-info">
                    <div class="embedph-info-wrap">
                        <div class="embedph-info-bar" />
                        <div class="embedph-info-body">
                            {...infoChildren}
                        </div>
                    </div>
                </div>
                <div class="embedph-play" onclick={ev => {
                    if(ev.target instanceof HTMLAnchorElement)
                        return;

                    const embed = new VideoEmbed(new playerType(metadata));
                    this.#element.replaceWith(embed.element);
                }}>
                    <div class="embedph-play-internal">
                        <i class="fa-solid fa-play fa-4x" />
                    </div>
                    <a class="embedph-play-external" target="_blank" rel="noopener" href={metadata.url}>
                        {`or watch on ${metadata.site_name}?`}
                    </a>
                </div>
            </div>
        </div>;
    }
}
